import { CustomDropdown, type CustomDropdownOption } from "@/components/ui/custom-dropdown";
import { handleTablistKeyDown } from "@/lib/tablistKeyboard";
import { Check, LockKeyhole } from "lucide-react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { SettingsGroupId, SettingsSection, SettingsTab } from "./registry";

type Group = { id: SettingsGroupId; label: string };
type TabState = "complete" | "locked" | "available";

/** Shared responsive settings navigation/content chrome. Persistence, dialogs,
 * and navigation guards deliberately remain owned by the caller. */
export function SettingsStyleShell({
  groups,
  sections,
  activeSection,
  activeTab,
  onSectionChange,
  onTabChange,
  isSectionDisabled = () => false,
  isTabDisabled = () => false,
  getDisabledTabTitle = () => "Complete the previous tab first",
  getTabState = () => "available",
  header,
  children,
  contentOverflow = "auto",
  contentTestId = "content-settings-hub",
  panelId = "settings-hub-panel",
  tabTestId = (section, tab) => `settings-tab-${section}-${tab}`,
  navTestId = (section) => `nav-settings-${section}`,
}: {
  groups: readonly Group[];
  sections: SettingsSection[];
  activeSection: string;
  activeTab: string | null;
  onSectionChange: (id: string) => void;
  onTabChange: (id: string) => void;
  isSectionDisabled?: (section: SettingsSection) => boolean;
  isTabDisabled?: (tab: SettingsTab) => boolean;
  getDisabledTabTitle?: (tab: SettingsTab) => string;
  getTabState?: (tab: SettingsTab) => TabState;
  header?: ReactNode;
  children: ReactNode;
  contentOverflow?: "auto" | "visible";
  contentTestId?: string;
  panelId?: string;
  tabTestId?: (section: string, tab: string) => string;
  navTestId?: (section: string) => string;
}) {
  const active = sections.find((section) => section.id === activeSection) ?? sections[0];
  if (!active) return null;
  // Disabled tabs remain visible so users understand what setup is missing;
  // only the enabled subset participates in arrow-key navigation.
  const tabs = active.tabs ?? [];
  const enabledTabs = tabs.filter((tab) => !isTabDisabled(tab));
  const activeTabSafe = enabledTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : enabledTabs[0]?.id ?? tabs[0]?.id ?? null;
  const contentRef = useRef<HTMLDivElement | null>(null);

  // The content pane is the scroll container on mobile. Reset it whenever a
  // section/tab changes so the new panel's heading cannot remain scrolled
  // underneath the fixed mobile section picker or sticky tab bar.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (typeof content.scrollTo === "function") {
      content.scrollTo({ top: 0, behavior: "auto" });
    } else {
      content.scrollTop = 0;
    }
  }, [active.id, activeTabSafe]);

  return (
    <>
      <nav className="hidden w-56 shrink-0 overflow-y-auto border-r border-border bg-muted/30 p-3 sm:block">
        {groups.map((group) => {
          const groupSections = sections.filter((section) => section.group === group.id);
          if (!groupSections.length) return null;
          return <div key={group.id} className="mb-4">
            <div className="px-2 pb-1.5 text-[12px] font-semibold uppercase tracking-wide text-foreground/50">{group.label}</div>
            <ul className="space-y-0.5">{groupSections.map((section) => {
              const Icon = section.icon;
               const selected = section.id === active.id;
               const disabled = isSectionDisabled(section);
               return <li key={section.id}><button type="button" id={`section-${section.id}`} data-testid={navTestId(section.id)} aria-disabled={disabled} title={disabled ? "Finish the required setup first" : undefined} onClick={() => !disabled && onSectionChange(section.id)} className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[15px] font-medium transition-colors ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${selected ? "bg-foreground text-background" : disabled ? "text-foreground/30" : "text-foreground hover:bg-muted"}`}>
                <Icon size={16} className="shrink-0" /><span className="truncate">{section.label}</span>
              </button></li>;
            })}</ul>
          </div>;
        })}
      </nav>
       <div className={`flex min-w-0 flex-1 flex-col ${contentOverflow === "auto" ? "min-h-0" : ""}`}>
        {header}
        <div className="border-b border-border px-4 py-2 sm:hidden">
          <CustomDropdown testId="select-settings-section" value={active.id} onChange={onSectionChange} textSizeClass="text-[15px]" options={groups.flatMap((group): CustomDropdownOption[] => {
            const groupSections = sections.filter((section) => section.group === group.id);
            return groupSections.length ? [{ label: group.label, value: `__group-${group.id}`, header: true }, ...groupSections.map((section) => ({ label: section.label, value: section.id }))] : [];
          })} />
        </div>
        {tabs.length > 0 && <div className="sticky top-0 z-10 border-b border-border bg-card px-3 sm:px-4"><div role="tablist" aria-label={`${active.label} tabs`} onKeyDown={(event) => handleTablistKeyDown(event, enabledTabs.map((tab) => tab.id), activeTabSafe ?? tabs[0].id, onTabChange)} className="flex flex-wrap items-center gap-1 overflow-x-auto no-scrollbar sm:flex-nowrap">
          {tabs.map((tab) => {
            const selected = tab.id === activeTabSafe;
            const disabled = isTabDisabled(tab);
             const state = disabled ? "locked" : getTabState(tab);
             return <button key={tab.id} type="button" role="tab" aria-selected={selected} aria-disabled={disabled || undefined} aria-label={`${tab.label}${state === "complete" ? " complete" : state === "locked" ? " locked" : ""}`} disabled={disabled} aria-controls={panelId} tabIndex={selected ? 0 : -1} data-testid={tabTestId(active.id, tab.id)} onClick={() => !disabled && onTabChange(tab.id)} title={disabled ? getDisabledTabTitle(tab) : undefined} className={`-mb-px inline-flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2 text-[15px] font-semibold transition-colors ${disabled ? "cursor-not-allowed border-transparent text-foreground/30" : selected ? "border-brand-sage bg-muted/40 text-foreground cursor-pointer" : "border-transparent text-foreground/60 hover:bg-muted/50 hover:text-foreground cursor-pointer"}`}>{state === "complete" && !selected && <Check size={15} aria-hidden="true" className="text-brand-sage" />}{state === "locked" && <LockKeyhole size={14} aria-hidden="true" />}{tab.label}</button>;
          })}
        </div></div>}
        <div
          id={panelId}
          role={tabs.length ? "tabpanel" : undefined}
          ref={contentRef}
          className={`scroll-pt-3 p-4 sm:p-5 ${
            contentOverflow === "auto"
              ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
              : "flex-none overflow-visible"
          }`}
          data-testid={contentTestId}
        >
          {children}
        </div>
      </div>
    </>
  );
}